import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const dashboardImages = [
  {
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_web-builder-418/artifacts/lx307nci_Screenshot%202026-09-07%20165609.png",
    title: "Resistance Trends & Department Risk",
    description: "Monitor resistance trends over time and visualize department-level risk heatmaps to identify hotspots.",
  },
  {
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_web-builder-418/artifacts/l24rxgwh_Screenshot%202026-09-07%20165511.png",
    title: "Stakeholder & Risk Metrics",
    description: "Track total stakeholders, change readiness percentage, high resistance counts, and open risks at a glance.",
  },
  {
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_web-builder-418/artifacts/8o0acebb_Screenshot%202026-09-07%20165544.png",
    title: "Recommended Actions",
    description: "AI-powered recommended actions highlight critical issues and prescribe next steps for leadership intervention.",
  },
  {
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_web-builder-418/artifacts/ld34rnw2_Screenshot%202026-09-07%20165439.png",
    title: "Executive Dashboard",
    description: "Real-time transformation health index with composite scoring across stakeholder readiness, risk management, and resistance trends.",
  },
];

const DashboardModal = ({ isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setCurrentIndex(0);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const next = () => setCurrentIndex((prev) => (prev + 1) % dashboardImages.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + dashboardImages.length) % dashboardImages.length);

  if (!mounted) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-md"
          style={{ zIndex: 9999 }}
          onClick={onClose}
          data-testid="dashboard-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-6xl w-full max-h-[90vh] bg-[#0A0A0F] border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_60px_-10px_rgba(236,72,153,0.4)] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="dashboard-modal-content"
          >
            <div className="absolute top-4 right-4 z-20">
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/10 flex items-center justify-center hover:bg-white/20 transition-all"
                data-testid="dashboard-modal-close"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 md:p-8 border-b border-white/10">
              <span className="font-heading font-bold uppercase tracking-widest text-xs text-pink-400 mb-2 block">
                Platform Preview
              </span>
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2" data-testid="dashboard-modal-title">
                {dashboardImages[currentIndex].title}
              </h3>
              <p className="font-body text-sm md:text-base text-slate-400" data-testid="dashboard-modal-description">
                {dashboardImages[currentIndex].description}
              </p>
            </div>

            <div className="relative p-6 md:p-8 flex-1 overflow-y-auto">
              <div className="relative rounded-xl overflow-hidden border border-white/10 bg-white">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    src={dashboardImages[currentIndex].url}
                    alt={dashboardImages[currentIndex].title}
                    className="w-full h-auto"
                    data-testid="dashboard-modal-image"
                  />
                </AnimatePresence>
              </div>

              <button
                onClick={prev}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 backdrop-blur-xl border border-white/20 flex items-center justify-center hover:bg-black/80 transition-all"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                onClick={next}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 backdrop-blur-xl border border-white/20 flex items-center justify-center hover:bg-black/80 transition-all"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="px-6 md:px-8 pb-6 md:pb-8 flex items-center justify-center gap-2">
              {dashboardImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`transition-all duration-300 rounded-full ${
                    idx === currentIndex
                      ? "w-8 h-2 bg-gradient-to-r from-pink-500 to-lime-500"
                      : "w-2 h-2 bg-white/20 hover:bg-white/40"
                  }`}
                  data-testid={`carousel-dot-${idx}`}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default DashboardModal;
